import { logger } from "./logger";
// YouTube utility functions for extracting video IDs and thumbnail URLs

/**
 * Extracts YouTube video ID from various YouTube URL formats
 * Supports:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/v/VIDEO_ID
 * - https://m.youtube.com/watch?v=VIDEO_ID
 * - https://www.youtube.com/shorts/VIDEO_ID (YouTube Shorts)
 */
export const extractYouTubeVideoId = (url: string): string | null => {
  if (!url) return null;

  try {
    const cleanUrl = url.trim();

    // Regex to match various YouTube URL formats
    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = cleanUrl.match(regExp);

    if (match && match[7] && match[7].length === 11) {
      return match[7];
    }

    // Alternative regex
    const shortRegExp = /youtu\.be\/([a-zA-Z0-9_-]{11})/;
    const shortMatch = cleanUrl.match(shortRegExp);

    if (shortMatch && shortMatch[1]) {
      return shortMatch[1];
    }

    // YouTube Shorts support
    const shortsRegExp = /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/;
    const shortsMatch = cleanUrl.match(shortsRegExp);

    if (shortsMatch && shortsMatch[1]) {
      return shortsMatch[1];
    }

    return null;
  } catch (error) {
    logger.warn("Error extracting YouTube video ID:", error);
    return null;
  }
};

/**
 * Generates YouTube thumbnail URL from video ID
 * @param videoId YouTube video ID
 * @param quality Thumbnail quality (default, mqdefault, hqdefault, sddefault, maxresdefault)
 * @returns Thumbnail image URL
 */
export const getYouTubeThumbnail = (
  videoId: string,
  quality: string = "mqdefault"
): string => {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};

/**
 * Extracts YouTube thumbnail URL directly from video URL
 * @param videoUrl YouTube video URL
 * @param quality Thumbnail quality
 * @returns Thumbnail image URL or null if invalid URL
 */
export const getYouTubeThumbnailFromUrl = (
  videoUrl: string,
  quality: string = "mqdefault"
): string | null => {
  const videoId = extractYouTubeVideoId(videoUrl);
  return videoId ? getYouTubeThumbnail(videoId, quality) : null;
};

/**
 * Validates if a URL is a valid YouTube URL
 * @param url URL to validate
 * @returns true if valid YouTube URL
 */
export const isValidYouTubeUrl = (url: string): boolean => {
  return extractYouTubeVideoId(url) !== null;
};
